import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

async function getUserCredits(userId: string): Promise<number> {
  const { data } = await supabaseAdmin
    .from("profiles")
    .select("credits")
    .eq("id", userId)
    .maybeSingle();
  return (data?.credits as number | null) ?? 0;
}

async function decrementUserCredits(userId: string): Promise<void> {
  await supabaseAdmin.rpc("increment_user_credits", {
    user_uuid: userId,
    amount: -1,
  });
}

export type NdaCategory =
  | "scope"
  | "duration"
  | "jurisdiction"
  | "remedies"
  | "non-compete"
  | "non-solicit"
  | "definition"
  | "carve-outs"
  | "return-of-info"
  | "penalties";

export type NdaRedFlag = {
  id: string;
  category: NdaCategory;
  title: string;
  severity: "low" | "medium" | "high";
  excerpt: string;
  whyItMatters: string;
  clarification: {
    ask: string;
    rewriteSuggestion: string;
    negotiationScript: string;
  };
};

export type NdaAnalysis = {
  overallRiskScore: number;
  riskLevel: "low" | "medium" | "high";
  partyBalance: "mutual" | "one-sided-recipient" | "one-sided-discloser" | "unclear";
  durationYears: number | null;
  redFlags: NdaRedFlag[];
  missingProtections: string[];
  keyTerms: {
    duration: string | null;
    jurisdiction: string | null;
    remedies: string | null;
    scope: string | null;
  };
  summary: string;
};

const SYSTEM_PROMPT = `You are legaldecoder's NDA Red Flags Detector — a friendly, plain-English NDA reviewer for non-lawyers. You DO NOT give legal advice; you flag risks and suggest clarifications.

Return a SINGLE JSON object matching exactly this schema (no extra fields, no commentary):

{
  "overallRiskScore": number (0-100; 0 = perfectly mutual + narrow + reasonable, 100 = walk away),
  "riskLevel": "low" | "medium" | "high",
  "partyBalance": "mutual" | "one-sided-recipient" | "one-sided-discloser" | "unclear",
  "durationYears": number | null,
  "summary": string (2 sentences, empathetic, plain English: what this NDA is and the headline risk),
  "redFlags": [
    {
      "id": string (kebab-case slug, unique),
      "category": "scope" | "duration" | "jurisdiction" | "remedies" | "non-compete" | "non-solicit" | "definition" | "carve-outs" | "return-of-info" | "penalties",
      "title": string (≤8 words, plain English),
      "severity": "low" | "medium" | "high",
      "excerpt": string (verbatim quote from the document, ≤220 chars; if unavailable use ""),
      "whyItMatters": string (1-2 sentences, plain English, no jargon),
      "clarification": {
        "ask": string (1 sentence: what to ask the other party for),
        "rewriteSuggestion": string (1-3 sentences: a fairer rewording the user can paste back),
        "negotiationScript": string (1-2 sentences: an exact email/script the user can send)
      }
    }
  ] (0-10 items),
  "missingProtections": string[] (0-6 items: standard NDA protections this document is missing, e.g. "no carve-out for information already public", "no return-of-information clause"),
  "keyTerms": {
    "duration": string | null (e.g. "5 years from disclosure"),
    "jurisdiction": string | null (e.g. "Delaware courts, exclusive"),
    "remedies": string | null (e.g. "injunctive relief + attorney fees + liquidated damages"),
    "scope": string | null (e.g. "all information disclosed, marked or unmarked")
  }
}

Focus areas (look for ALL of these):
- Overbroad definition of "Confidential Information" (e.g. covers everything, including publicly known info)
- Excessive duration (>3 years for general business info; perpetual; "forever")
- Missing standard carve-outs (already known, independently developed, publicly available, required by law)
- Hidden non-compete or non-solicit baked into the NDA
- One-sided obligations (only recipient has duties; discloser carries no risk)
- Aggressive remedies: liquidated damages, attorneys' fees one-way, indemnification, injunctive relief without bond
- Inconvenient jurisdiction / forum / choice of law
- Return/destruction obligations that are impossible to comply with
- Penalty or liquidated damages clauses with arbitrary dollar amounts
- Anti-disparagement language hiding inside an NDA

Tone: friendly, calm, like a smart friend ("here's what to watch for"). Never invent quotes — only quote text actually present in the document. If the document is too short or not an NDA, return overallRiskScore 0, riskLevel "low", an empty redFlags array, and explain in summary.`;

const analyzeInput = z.object({
  text: z.string().min(40, "Paste at least 40 characters").max(40000),
  title: z.string().min(1).max(120).optional(),
});

type SubscriptionSnapshot = {
  status: string | null;
  current_period_end: string | null;
  product_id?: string | null;
};

function isProSubscription(sub: SubscriptionSnapshot | null | undefined): boolean {
  if (!sub) return false;
  const periodOk = !sub.current_period_end || new Date(sub.current_period_end) > new Date();
  const statusOk =
    (["active", "trialing", "past_due"].includes(sub.status as string) && periodOk) ||
    (sub.status === "canceled" && periodOk);
  return statusOk && (sub.product_id === "pro_plan" || sub.product_id === "teams_plan");
}

function deriveTitle(text: string): string {
  const firstLine =
    text
      .split("\n")
      .find((l) => l.trim().length > 0)
      ?.trim() ?? "NDA";
  return firstLine.slice(0, 80);
}

export const analyzeNdaRedFlags = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => analyzeInput.parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;

    // Credits check
    const credits = await getUserCredits(userId);
    if (credits <= 0) {
      return {
        ok: false as const,
        reason: "quota_exceeded" as const,
        credits,
      };
    }

    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) throw new Error("LOVABLE_API_KEY is not configured");

    const aiRes = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash",
        messages: [
          { role: "system", content: SYSTEM_PROMPT },
          { role: "user", content: `Analyze this NDA:\n\n${data.text}` },
        ],
        response_format: { type: "json_object" },
      }),
    });

    if (!aiRes.ok) {
      const body = await aiRes.text();
      console.error("NDA detector AI error", { status: aiRes.status, body });
      if (aiRes.status === 429) return { ok: false as const, reason: "rate_limited" as const };
      if (aiRes.status === 402)
        return { ok: false as const, reason: "ai_credits_exhausted" as const };
      throw new Error("NDA detector unavailable");
    }

    const aiJson = await aiRes.json();
    const content = aiJson.choices?.[0]?.message?.content;
    if (!content) throw new Error("Empty AI response");

    let parsed: NdaAnalysis;
    try {
      const raw = JSON.parse(content);
      parsed = {
        overallRiskScore:
          typeof raw.overallRiskScore === "number"
            ? Math.max(0, Math.min(100, raw.overallRiskScore))
            : 0,
        riskLevel: ["low", "medium", "high"].includes(raw.riskLevel) ? raw.riskLevel : "low",
        partyBalance: ["mutual", "one-sided-recipient", "one-sided-discloser", "unclear"].includes(
          raw.partyBalance,
        )
          ? raw.partyBalance
          : "unclear",
        durationYears: typeof raw.durationYears === "number" ? raw.durationYears : null,
        summary: typeof raw.summary === "string" ? raw.summary : "",
        redFlags: Array.isArray(raw.redFlags) ? raw.redFlags.slice(0, 10) : [],
        missingProtections: Array.isArray(raw.missingProtections)
          ? raw.missingProtections.slice(0, 6)
          : [],
        keyTerms: {
          duration: raw.keyTerms?.duration ?? null,
          jurisdiction: raw.keyTerms?.jurisdiction ?? null,
          remedies: raw.keyTerms?.remedies ?? null,
          scope: raw.keyTerms?.scope ?? null,
        },
      };
    } catch {
      throw new Error("AI returned malformed output");
    }

    // Persist into analyses table so it shows up in /account history and can be saved
    // as a contract version. Wrap the NDA payload in the AnalysisResult shape so the
    // shared rendering pipeline handles it.
    const wrappedResult = {
      verdict: { summary: parsed.summary, risk: parsed.riskLevel },
      riskScore: parsed.overallRiskScore,
      keyTerms: [
        parsed.keyTerms.duration ? `Duration: ${parsed.keyTerms.duration}` : null,
        parsed.keyTerms.jurisdiction ? `Jurisdiction: ${parsed.keyTerms.jurisdiction}` : null,
        parsed.keyTerms.remedies ? `Remedies: ${parsed.keyTerms.remedies}` : null,
        parsed.keyTerms.scope ? `Scope: ${parsed.keyTerms.scope}` : null,
        `Party balance: ${parsed.partyBalance}`,
      ].filter((s): s is string => !!s),
      redFlags: parsed.redFlags.map((f) => f.title),
      clauseExplanations: parsed.redFlags.map((f) => ({
        title: f.title,
        what: f.excerpt || "Clause not directly quoted in document.",
        why: f.whyItMatters,
        suggestion: f.clarification.ask,
        negotiationScript: f.clarification.negotiationScript,
        severity: f.severity === "high" ? "warning" : f.severity === "medium" ? "caution" : "info",
      })),
      // Stash the full NDA detail for the dedicated UI
      ndaDetail: parsed,
    } as const;

    const row = {
      user_id: userId,
      status: "completed",
      risk_level: parsed.riskLevel,
      risk_score: parsed.overallRiskScore,
      tool_slug: "nda-detector",
      title: data.title ?? deriveTitle(data.text),
      result: wrappedResult,
    };

    const { data: inserted } = await supabaseAdmin
      .from("analyses")
      .insert([row])
      .select("id")
      .single();

    await decrementUserCredits(userId);

    return {
      ok: true as const,
      result: parsed,
      analysisId: (inserted?.id as string) ?? null,
      extractedText: data.text,
      creditsRemaining: Math.max(0, credits - 1),
    };
  });
